import { ref, computed } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { useDebouncedSearchQuery } from "@utils/composables/filters/useDebouncedSearchQuery";
import type { Account, AccountOwner, HubspotCompany } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const accountLabel = (account: Account): string =>
  account.master_account_name ? `${account.master_account_name} → ${account.name}` : account.name;

export const useAccountsStore = defineStore("accounts", () => {
  // state
  const accounts = ref<Account[]>([]);
  const accountsCount = ref(0);
  const currentPage = ref(1);
  const searchQuery = ref("");
  const typeFilter = ref<string[]>([]);
  const ownersFilter = ref<number[]>([]);
  const owners = ref<AccountOwner[]>([]);
  const sortColumn = ref("name");
  const sortDirection = ref<"asc" | "desc">("asc");

  // getters
  const availableAccountsOptions = computed(() =>
    accounts.value.map((account) => ({ label: accountLabel(account), value: account.id })),
  );

  // actions
  async function fetch(page: number | null = null) {
    const params = new URLSearchParams();
    if (page) {
      params.append("page", String(page));
      if (searchQuery.value.trim()) params.append("search", searchQuery.value.trim());
      typeFilter.value.forEach((type) => params.append("types[]", type));
      ownersFilter.value.forEach((id) => params.append("owner_ids[]", String(id)));
      params.append("sort_column", sortColumn.value);
      params.append("sort_direction", sortDirection.value);
    }
    const query = params.toString();
    const { data } = await useMyFetch(`/accounts${query ? `?${query}` : ""}`).json<{
      accounts: Account[];
      accounts_count: number;
    }>();
    if (data.value) {
      accounts.value = data.value.accounts;
      accountsCount.value = data.value.accounts_count;
    }
    if (page) currentPage.value = page;
    return accounts.value;
  }

  const clearFilters = async () => {
    typeFilter.value = [];
    ownersFilter.value = [];
    await fetch(1);
  };

  const resetAllFilters = () => {
    resetSearchQuery();
    typeFilter.value = [];
    ownersFilter.value = [];
    sortColumn.value = "name";
    sortDirection.value = "asc";
    currentPage.value = 1;
    accounts.value = [];
    accountsCount.value = 0;
  };

  const { resetSearchQuery } = useDebouncedSearchQuery(searchQuery, () => fetch(1));

  // Flat fetch kept out of `accounts` so the paginated tree of the admin page
  // is not clobbered when a dialog needs the list of possible parents.
  async function fetchMasterAccounts() {
    const { data } = await useMyFetch("/accounts").json<{ accounts: Account[] }>();
    return (data.value?.accounts ?? []).filter((account) => !account.master_account_id && !account.freemium);
  }

  async function fetchOwners() {
    const { data, error } = await useMyFetch("/accounts/owners").json<{ owners: AccountOwner[] }>();
    if (error.value) throw error.value;
    owners.value = data.value?.owners ?? [];
    return owners.value;
  }

  async function searchHubspotCompanies(query: string): Promise<HubspotCompany[]> {
    const { data, error } = await useMyFetch(`/pricer/hubspot/companies?query=${encodeURIComponent(query)}`).json<{
      companies: Array<{ id: string; name: string }>;
    }>();
    if (error.value) throw error.value;
    return (data.value?.companies ?? []).map(({ id, name }) => ({ id: Number(id), name }));
  }

  async function createAccount(payload: Partial<Account>) {
    const { data, error } = await useMyFetch("/accounts").post({ account: payload }).json<Account>();
    if (error.value) throw error.value;

    captureEvent("account_created", { account_id: data.value?.id, is_sub_account: payload.master_account_id != null });
    if (data.value) {
      accounts.value.push(data.value);
      accounts.value.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data.value;
  }

  async function updateAccount(id: number, payload: Partial<Account>) {
    const { data, error } = await useMyFetch(`/accounts/${id}`).put({ account: payload }).json<Account>();
    if (error.value) throw error.value;

    captureEvent("account_updated", { account_id: id });
    if (data.value) {
      const index = accounts.value.findIndex((a) => a.id === id);
      if (index !== -1) accounts.value[index] = data.value;
      accounts.value.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data.value;
  }

  async function deleteAccount(id: number) {
    const { error } = await useMyFetch(`/accounts/${id}`).delete();
    if (error.value) throw error.value;

    captureEvent("account_deleted", { account_id: id });

    accounts.value = accounts.value.filter((a) => a.id !== id);
  }

  // Always a standalone master account: an account chosen in the UI only seeds
  // the prospect list, it never becomes the parent.
  async function createFreemiumAccount({
    name,
    prospects,
    clientId,
  }: {
    name: string;
    prospects: Array<{ email: string; first_name?: string; last_name?: string }>;
    clientId?: string;
  }) {
    const { data, error } = await useMyFetch("/accounts/freemium")
      .post({ account: { name }, prospects, client_id: clientId })
      .json<Account>();
    if (error.value) throw error.value;

    captureEvent("freemium_account_created", { account_id: data.value?.id, prospects_count: prospects.length });
    if (data.value) {
      accounts.value.push(data.value);
      accounts.value.sort((a, b) => a.name.localeCompare(b.name));
    }
    return data.value;
  }

  return {
    // state
    accounts,
    accountsCount,
    currentPage,
    searchQuery,
    typeFilter,
    ownersFilter,
    owners,
    sortColumn,
    sortDirection,
    // getters
    availableAccountsOptions,
    // actions
    fetch,
    fetchMasterAccounts,
    fetchOwners,
    searchHubspotCompanies,
    clearFilters,
    resetAllFilters,
    createAccount,
    updateAccount,
    deleteAccount,
    createFreemiumAccount,
  };
});
